import React, {useEffect, useState} from 'react';
import {FormLabel, Select, Switch, TextField, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button} from 'reactstrap';
import DocusignApiService from '../../api/DocusignApiService';
import SettingsApiService from '../../api/SettingsApiService';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';

export default function AdminSettings(props) {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const tempConsentCode = searchParams.get('code');

    const {isMobile, setSnackbarProperties} = props;

    const docusignApiService = new DocusignApiService();
    const settingsApiService = new SettingsApiService();
    const navigate = useNavigate();

    const [isDocusignEnabled, setIsDocusignEnabled] = useState(false);
    const [isDocusignEnabledSetting, setIsDocusignEnabledSetting] = useState(undefined);
    const [currentTemplateId, setCurrentTemplateId] = useState('');
    const [templates, setTemplates] = useState([]);
    const [docusignTemplateIdSetting, setDocusignTemplateIdSetting] = useState(undefined);
    const [currentDocusignConsent, setCurrentDocusignConsent] = useState(false);

    const [isSmsEnabled, setIsSmsEnabled] = useState(false);
    const [smsEnabledSetting, setSmsEnabledSetting] = useState(undefined);
    const [smsSenderName, setSmsSenderName] = useState('');
    const [smsSenderNameSetting, setSmsSenderNameSetting] = useState(undefined);
    const [reminderDaysBefore, setReminderDaysBefore] = useState('');
    const [reminderDaysBeforeSetting, setReminderDaysBeforeSetting] = useState(undefined);

    useEffect(() => {
        if(!authenticationManager.isAuthorized) {
            redirectToLogin(navigate);
        }

        void getDocusignAccountTemplates();

        void getDocusignEnabled();
        void isConsented();
        void getSmsSettings();

        // After getting consent we'll have a temporary code. If we have it, we need to setup the docusign user
        if (tempConsentCode) {
            void setupDocusignUser(tempConsentCode);
        }
    }, [tempConsentCode]);

    async function isConsented() {
        const response = await docusignApiService.isConsented();

        if(response.success) {
            setCurrentDocusignConsent(response.data);
        } else {
            setSnackbarProperties('Error occurred when trying to check Docusign data.', 'error');
        }
    }

    async function getDocusignAccountTemplates() {
        const docusignAccountId = await settingsApiService.getSetting('DocusignAccountId');

        if (docusignAccountId.value) {
            const docusignTemplates = (await docusignApiService.getDocusignAccountTemplates()).data;
            setTemplates(docusignTemplates);

            const docusignTemplateId = await settingsApiService.getSetting('DocusignTemplateId');

            setDocusignTemplateIdSetting(docusignTemplateId);
            setCurrentTemplateId(docusignTemplates.find((template) => template.templateId === docusignTemplateId.value)?.templateId);
        }
    }

    async function getDocusignEnabled() {
        const response = await settingsApiService.getSetting('DocusignEnabled');

        setIsDocusignEnabledSetting(response);
        setIsDocusignEnabled(response.value === 'true');
    }

    async function getSmsSettings() {
        const enabled = await settingsApiService.getSetting('SmsEnabled');
        setSmsEnabledSetting(enabled);
        setIsSmsEnabled(enabled.value === 'true');

        const senderName = await settingsApiService.getSetting('SmsSenderName');
        setSmsSenderNameSetting(senderName);
        setSmsSenderName(senderName.value ?? '');

        const daysBefore = await settingsApiService.getSetting('PaymentReminderDaysBefore');
        setReminderDaysBeforeSetting(daysBefore);
        setReminderDaysBefore(daysBefore.value ?? '');
    }

    async function saveReminderDaysBefore() {
        const daysBefore = Number(reminderDaysBefore);

        if (reminderDaysBefore.trim() === '' || !Number.isInteger(daysBefore)) {
            setSnackbarProperties('Enter a whole number of days, or 0 to turn reminders off.', 'error');
            setReminderDaysBefore(reminderDaysBeforeSetting.value ?? '');
            return;
        }

        reminderDaysBeforeSetting.value = daysBefore.toString();

        const settingUpdateResponse = await settingsApiService.updateSetting(reminderDaysBeforeSetting);

        if (settingUpdateResponse) {
            setSnackbarProperties(
                daysBefore <= 0 ? 'Rent reminders turned off' : `Rent reminders will be sent ${daysBefore} day${daysBefore === 1 ? '' : 's'} before rent is due`,
                'success'
            );
        } else {
            setSnackbarProperties('Error occurred when trying to save the reminder schedule.', 'error');
        }
    }

    async function handleSmsEnabledChange(event) {
        const updatedEnabledStatus = event.target.checked;
        smsEnabledSetting.value = updatedEnabledStatus.toString();

        const settingUpdateResponse = await settingsApiService.updateSetting(smsEnabledSetting);

        if (settingUpdateResponse) {
            setSnackbarProperties(`Text messaging ${updatedEnabledStatus ? 'enabled' : 'disabled'} successfully`, 'success');
        } else {
            setSnackbarProperties('Error occurred when trying to change text messaging.', 'error');
        }

        setIsSmsEnabled(updatedEnabledStatus);
    }

    async function saveSmsSenderName() {
        smsSenderNameSetting.value = smsSenderName;

        const settingUpdateResponse = await settingsApiService.updateSetting(smsSenderNameSetting);

        if (settingUpdateResponse) {
            setSnackbarProperties('Sender name saved successfully', 'success');
        } else {
            setSnackbarProperties('Error occurred when trying to save the sender name.', 'error');
        }
    }

    async function getDocusignConsent() {
        const response = await docusignApiService.getDocusignConsent();

        if(!response.success) {
            setSnackbarProperties('Error occurred when trying to get Docusign Consent.', 'error');
        }
    }

    async function setupDocusignUser(tempConsentCode) {
        const response = await docusignApiService.setupDocusignUser(tempConsentCode);

        if(!response.success) {
            setSnackbarProperties('Error occurred when trying to setup Docusign User.', 'error');
        }

        // Remove the temp code from the URL.
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, document.title, url.pathname);
    }

    async function handleChange(event) {
        let updatedEnabledStatus = event.target.checked;
        isDocusignEnabledSetting.value = updatedEnabledStatus.toString();
        const settingUpdateResponse = await settingsApiService.updateSetting(isDocusignEnabledSetting);
        if(settingUpdateResponse) {
            setSnackbarProperties(`Docusign ${updatedEnabledStatus ? 'Enabled' : 'Disabled'} successfully`, 'success');
        } else {
            setSnackbarProperties('Error occurred when trying to change Docusign Enabled.', 'error');
        }

        setIsDocusignEnabled(updatedEnabledStatus);
    }

    async function changeTemplate(event) {
        let docusignTemplateSetting = docusignTemplateIdSetting;
        docusignTemplateSetting.value = event.target.value;

        const changeTemplateResponse  = await settingsApiService.updateSetting(docusignTemplateIdSetting);

        if(changeTemplateResponse) {
            setSnackbarProperties('Template changed successfully', 'success');
        } else {
            setSnackbarProperties('Error occurred when trying to change template.', 'error');
        }

        setDocusignTemplateIdSetting(docusignTemplateSetting);
        setCurrentTemplateId(event.target.value);
    }

    return(
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <h1>Settings</h1>
            </Grid>
            <Grid item xs={12}>
                <>
                    <FormLabel>Docusign Enabled</FormLabel>
                    <Switch
                        checked={isDocusignEnabled}
                        onChange={handleChange}
                        color="primary"
                    />
                </>
            </Grid>
            <Grid item xs={12}>
                <Button className={isMobile ? 'mobile-modal-button' : ''} autoFocus variant='contained'
                    onClick={getDocusignConsent}>
                    {currentDocusignConsent ? 'Change Docusign Consent' : 'Get Docusign Consent' }
                </Button>
            </Grid>
            <Grid item xs={12}>
                <>
                    <FormLabel>Text Messaging Enabled</FormLabel>
                    <Switch
                        checked={isSmsEnabled}
                        onChange={handleSmsEnabledChange}
                        color="primary"
                    />
                </>
                <Typography variant={'body2'}>
                    While this is off, no text messages are created or sent, and tenants are not offered the option to sign up. Leave it off until your Twilio number and A2P 10DLC registration are approved.
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant={'body2'} sx={{marginBottom: '5px'}}>
                    Text Sender Name — the business name shown at the start of every text. Carriers require the sender to be identifiable.
                </Typography>
                <TextField
                    value={smsSenderName}
                    onChange={(event) => setSmsSenderName(event.target.value)}
                    onBlur={saveSmsSenderName}
                    variant={'standard'}
                    sx={{width: '300px'}}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant={'body2'} sx={{marginBottom: '5px'}}>
                    Rent Reminder — how many days before rent is due to remind tenants by email and text. Set to 0 to turn reminders off.
                </Typography>
                <TextField
                    value={reminderDaysBefore}
                    onChange={(event) => setReminderDaysBefore(event.target.value)}
                    onBlur={saveReminderDaysBefore}
                    type={'number'}
                    variant={'standard'}
                    sx={{width: '300px'}}
                />
            </Grid>
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <Typography variant='p2'>
                        Docusign Templates
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Select
                        disabled={!currentDocusignConsent || !isDocusignEnabled}
                        value={currentTemplateId}
                        onChange={(event) => changeTemplate(event)}
                        variant={'standard'}
                        sx={{width: '300px'}}
                    >
                        {templates?.map((x) => {
                            return (<MenuItem key={x.templateId} value={x.templateId}>{x.name}</MenuItem>);
                        })}
                        {templates.length === 0 && currentDocusignConsent &&
                            <MenuItem key={0} value={0}>No templates found</MenuItem>
                        }
                        {!currentDocusignConsent &&
                            <MenuItem key={0} value={0}>Need to get Docusign consent</MenuItem>
                        }
                    </Select>
                </Grid>
            </Grid>
        </Grid>
    );
}

AdminSettings.propTypes = {
    isMobile: PropTypes.bool,
    setSnackbarProperties: PropTypes.func,
};
